import React, { useState } from "react";
import { Button } from "reactstrap";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { FileDown } from "lucide-react";

/**
 * Reusable ExportToExcel Component
 * @param {Function} fetchDataFunction - Async function that fetches data with filters and pagination
 * @param {Object} filters - Current filters to apply when fetching data
 * @param {String} fileName - Name of the exported Excel file (without extension)
 * @param {Function} dataTransformer - Optional function to transform data before export
 * @param {String} buttonText - Text to display on button (default: "Export")
 * @param {String} buttonSize - Size of button (sm, md, lg)
 * @param {String} buttonColor - Color of button (primary, secondary, success, etc.)
 */
const ExportToExcel = ({
  fetchDataFunction,
  filters = {},
  fileName = "export",
  dataTransformer = null,
  buttonText = "Export",
  buttonSize = "sm",
  buttonColor = "success",
  disabled = false,
}) => {
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    try {
      setExporting(true);
      toast.loading("Preparing data for export...", { id: "export" });

      // Fetch all data with large page size (1000)
      const response = await fetchDataFunction({
        ...filters,
        pageNumber: 1,
        pageSize: 1000,
      });

      if (!response || !response.data || response.data.length === 0) {
        toast.error("No data to export", { id: "export" });
        return;
      }

      let dataToExport = response.data;

      // Transform data if transformer function provided
      if (dataTransformer && typeof dataTransformer === "function") {
        dataToExport = dataTransformer(dataToExport);
      }

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(dataToExport);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

      // Auto-size columns
      const maxWidth = 30;
      const colWidths = [];
      if (dataToExport.length > 0) {
        const firstRow = dataToExport[0];
        Object.keys(firstRow).forEach((key) => {
          const columnData = dataToExport.map((row) => row[key]?.toString() || "");
          const maxLength = Math.max(
            key.length,
            ...columnData.map((val) => val.length)
          );
          colWidths.push({ wch: Math.min(maxLength + 2, maxWidth) });
        });
        worksheet["!cols"] = colWidths;
      }

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().split("T")[0];
      const fullFileName = `${fileName}_${timestamp}.xlsx`;

      // Export file
      XLSX.writeFile(workbook, fullFileName);

      toast.success(
        `Successfully exported ${dataToExport.length} records`,
        { id: "export" }
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data", { id: "export" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      color={buttonColor}
      size={buttonSize}
      onClick={handleExport}
      disabled={disabled || exporting}
      className="d-flex align-items-center gap-1"
    >
      <FileDown size={16} />
      {exporting ? "Exporting..." : buttonText}
    </Button>
  );
};

export default ExportToExcel;
